import { createReducer } from '@reduxjs/toolkit';
import uuidv4 from 'uuid/v4';
import {
  fetchConfigSuccess,
  setUserJourney,
  startAgain,
  setJointJourney,
  setSecondaryApplicant,
  updateReferrer,
  updateNativeAppData,
  updateLoanType,
  updateJointBankId,
  enableDipToFullJourney,
  disableDipToFullJourney,
  enablePostDIPReview,
  disablePostDIPReview
} from 'actionCreators/config';
import { bankIdSuccessResponse, bankIdSelectorSuccess } from 'actionCreators/search';
import { updateCustomerId, updateCustomerInfo } from 'actionCreators/customer';
import { linkCodeSuccessResponse, updateJointLinkCode } from 'actionCreators/authCode';
import {
  PRE_ASSESSED_JOURNEY,
  CONGRATS_REFERRER,
  NTB_JOURNEY,
  JOINT_TYPE,
  SKIP_DIP_PROCESS_FALSE
} from 'constants/general';

const initialState = {
  bankId: null,
  brandId: 'VM',
  userTrackingId: uuidv4(),
  configUrls: {},
  sourceId: null,
  accessToken: null,
  journey: NTB_JOURNEY,
  origin: null,
  hasStartAgain: false,
  isJoint: false,
  isPostDIPReview: false,
  linkCode: null,
  originBrandId: null,
  jointLinkCode: null,
  isSecondaryApplicant: false,
  nativeAppData: null,
  jointBankId: null,
  inEditMode: false,
  branchSortCode: null,
  dipToFullJourney: false
};

const configReducer = createReducer(initialState, (builder) => {
  builder.addCase(fetchConfigSuccess, (state, action) => {
    state.configUrls = action.payload;
  });

  builder.addCase('setQueryParam', (state, action) => {
    const {
      bankId,
      brandId,
      accessToken,
      userTrackingId,
      sourceId,
      referrer,
      customerId,
      loanType,
      origin,
      testCustomerDetails,
      originBrandId,
      jointLinkCode,
      jointBankId,
      inEditMode,
      branchSortCode,
      skipDecisionInPrinciple,
      endUserIp
    } = action.payload;

    /**
     * customerInfo screens sending editmode as false|true.Hence used with typeof to checked the value.
     */
    if (typeof inEditMode === 'boolean') {
      return {
        ...state,
        inEditMode
      };
    }
    return {
      ...state,
      testCustomerDetails,
      brandId: brandId || state.brandId,
      bankId: bankId || state.bankId,
      userTrackingId: userTrackingId || state.userTrackingId,
      accessToken: accessToken || state.accessToken,
      sourceId: sourceId || state.sourceId,
      referrer: referrer || state.referrer,
      customerId: customerId || state.customerId,
      loanType: loanType || state.loanType,
      origin,
      originBrandId,
      journey: referrer === CONGRATS_REFERRER ? PRE_ASSESSED_JOURNEY : state.journey,
      isJoint: loanType === JOINT_TYPE || !!jointLinkCode,
      jointLinkCode,
      isSecondaryApplicant: !!jointLinkCode,
      jointBankId,
      inEditMode: inEditMode || state.inEditMode,
      branchSortCode: branchSortCode || state.branchSortCode,
      skipDecisionInPrinciple: !(skipDecisionInPrinciple === SKIP_DIP_PROCESS_FALSE),
      endUserIp
    };
  });

  builder.addCase(bankIdSuccessResponse, (state, action) => {
    state.bankId = action.payload;
  });

  builder.addCase(updateCustomerId, (state, action) => {
    state.customerId = action.payload.customerId;
  });

  builder.addCase(updateCustomerInfo, (state, action) => {
    const { token, customerId, referrer } = action.payload;
    state.accessToken = token;
    state.customerId = customerId;
    state.referrer = referrer;
  });

  builder.addCase(setUserJourney, (state, action) => {
    state.journey = action.payload;
  });

  builder.addCase(enablePostDIPReview, (state) => {
    state.isPostDIPReview = true;
  });

  builder.addCase(disablePostDIPReview, (state) => {
    state.isPostDIPReview = false;
  });

  builder.addCase(startAgain, (state, action) => {
    state.hasStartAgain = action.payload;
  });

  builder.addCase(setJointJourney, (state, action) => {
    state.isJoint = action.payload;
  });

  builder.addCase(linkCodeSuccessResponse, (state, action) => {
    state.isSecondaryApplicant = true;
    state.jointLinkCode = action.payload;
    state.accessToken = null;
    state.customerId = null;
    state.referrer = null;
  });

  builder.addCase(setSecondaryApplicant, (state, action) => {
    state.isSecondaryApplicant = action.payload;
  });

  builder.addCase(updateJointLinkCode, (state, action) => {
    state.jointLinkCode = action.payload;
  });

  builder.addCase(updateReferrer, (state, action) => {
    state.referrer = action.payload;
  });

  builder.addCase(updateNativeAppData, (state, action) => {
    const { xbpi_client_context, xbpi_service_context, is_native } = action.payload;
    state.nativeAppData = {
      clientContext:
        typeof xbpi_client_context === 'string'
          ? JSON.parse(xbpi_client_context)
          : xbpi_client_context,
      serviceContext: xbpi_service_context,
      isNative: is_native
    };
  });

  builder.addCase(updateLoanType, (state, action) => {
    state.loanType = action.payload;
  });

  builder.addCase(bankIdSelectorSuccess, (state, action) => {
    state.bankId = action.payload;
  });

  builder.addCase(updateJointBankId, (state, action) => {
    state.jointBankId = action.payload;
  });

  builder.addCase(enableDipToFullJourney, (state) => {
    state.dipToFullJourney = true;
  });

  builder.addCase(disableDipToFullJourney, (state) => {
    state.dipToFullJourney = false;
  });
});

export { configReducer };
